/**
 * PC-v2.1-A — LAS CIFRAS DE OT, CON LAS MISMAS PALABRAS EN EL PLANNER INTERNO Y EN EL CALENDARIO DEL CLIENTE
 *
 * El servidor ya las calcula (`backend/src/ejecucion/clasificacion-ot.ts`, una sola clasificación por estado
 * efectivo). Aquí sólo se nombran: las cinco situaciones EXCLUYENTES (suman el total de OT no anuladas) y, aparte,
 * los dos indicadores TRANSVERSALES (se solapan con ellas y no se suman). Cada panel las pinta a su manera.
 * Las tareas van en su propio bloque: nunca se mezclan con las OT.
 *
 * PC-v2.2-C2.1: G5 (las cinco situaciones en una barra) también es de aquí, para que el Calendario del cliente y el
 * Planner interno lo pinten igual.
 */
import type { ReactNode } from 'react';

import type { ResumenOts } from '../api.ts';
import { BarraApilada, type Trama } from './graficos.tsx';

export interface CifraOt {
  readonly clave: string;
  readonly texto: string;
  readonly valor: number;
}

export function situacionesOt(o: ResumenOts): readonly CifraOt[] {
  return [
    { clave: 'previstas', texto: 'Previstas', valor: o.previstas },
    { clave: 'en-curso', texto: 'En curso', valor: o.enCurso },
    { clave: 'finalizadas', texto: 'Finalizadas, pendientes de verificar', valor: o.finalizadasSinVerificar },
    { clave: 'verificadas', texto: 'Verificadas', valor: o.verificadas },
    { clave: 'no-realizadas', texto: 'No realizadas', valor: o.noRealizadas },
  ];
}

export function indicadoresOt(o: ResumenOts): readonly CifraOt[] {
  return [
    { clave: 'con-incidencia', texto: 'Con incidencia', valor: o.conIncidencia },
    { clave: 'fuera-de-plazo', texto: 'Fuera de plazo', valor: o.fueraDePlazo },
  ];
}

/** Color y trama de cada situación en G5 (nunca sólo el color). */
const ESTILO_SITUACION: Record<string, { color: string; trama: Trama }> = {
  previstas: { color: '#64748b', trama: 'lisa' },
  'en-curso': { color: '#0f6ea8', trama: 'rayas' },
  finalizadas: { color: '#9a6008', trama: 'puntos' },
  verificadas: { color: '#167854', trama: 'inversa' },
  'no-realizadas': { color: '#a5292b', trama: 'cuadros' },
};

/** G5 — las cinco situaciones excluyentes de las OT del periodo, en una barra que suma el total. */
export function GraficoSituacionesOt({ o }: { o: ResumenOts }): ReactNode {
  return (
    <BarraApilada
      etiqueta={`Situación de las ${String(o.total)} órdenes del periodo: ${situacionesOt(o).map((c) => `${c.texto}, ${String(c.valor)}`).join('; ')}`}
      total={o.total}
      segmentos={situacionesOt(o).map((c) => ({ clave: c.clave, nombre: c.texto, valor: c.valor, ...ESTILO_SITUACION[c.clave]! }))}
    />
  );
}
